import { Injectable, Inject } from "@angular/core";
import { Subject } from "rxjs";
import { StorageService } from "./storage.service";
import { HttpHeaders, HttpClient, HttpHeaderResponse } from "@angular/common/http";
import { ClaimsService } from "./claims.service";
import { parseJwt } from "../utilities/jwt-utilities";

@Injectable()
export class AuthService {
    isRefreshing: boolean = false;
    refreshSubject: Subject<string> = new Subject();
    refreshTimeout;
    timeout = 1000 * 60 * 15; // refresh every 15 minutes

    constructor(
        private storageService: StorageService,
        private claimsService: ClaimsService,
        private http: HttpClient,
        @Inject("BASE_URL") private baseUrl: string
    ) {}

    private storeToken(httpResponse: any): void {
        this.storageService.removeItem("token");
        this.storageService.removeItem("refresh_token");
        this.storageService.removeItem("token_expires");

        this.storageService.setItem("token", httpResponse.access_token);
        this.storageService.setItem("idtoken", httpResponse.id_token);
        
        let expires_in = new Date();
        expires_in.setSeconds(expires_in.getSeconds() + httpResponse.expires_in);
        this.storageService.setItem("token_expires", expires_in.toUTCString());
    }

    private refreshInterval() {
        this.refreshTimeout = setTimeout(() => {
            this.refresh()
                .then((result) => {
                    this.refreshInterval();
                })
                .catch((error) => {
                    this.refreshInterval();
                });
        }, this.timeout);
    }

    refresh(force: boolean = false): Promise<string> {        
        const url = this.baseUrl + "identity/refreshOAuth";
        let token = this.storageService.getItem("refresh_token");

        if (this.isRefreshing && !force) {
            return new Promise((resolve, reject) => {
                this.refreshSubject.subscribe((value) => {
                    resolve(value);
                });
            });
        }

        this.isRefreshing = true;
        this.refreshSubject = new Subject<string>();

        let promise = new Promise<string>((resolve, reject) => {
            this.http
                .post(url, { token: token })
                .toPromise()
                .then((res: any) => {
                    console.log(
                        "Refresh method executed successfully. " +
                            res.access_token
                    );
                    this.storeToken(res);
                    this.isRefreshing = false;
                    this.refreshSubject.next(res.access_token);
                    resolve(res.access_token);
                })
                .catch((reason) => {
                    reject(reason);
                });
        });

        return promise;
    }

    exchange(code: string, state: string): Promise<any> {
        const encodedCode: string = encodeURIComponent(code);
        const encodedState: string = encodeURIComponent(state);
        const url = `${this.baseUrl}identity/exchange?code=${encodedCode}&state=${encodedState}`;

        let promise = new Promise((resolve, reject) => {
            this.http
                .get(url)
                .toPromise()
                .then((res: any) => {
                    this.storeToken(res);
                    const idToken = res.id_token;
                    const userId = parseJwt(idToken);

                    this.storageService.setItem(
                        "user",
                        JSON.stringify({
                            username: userId.email,
                            name: userId.name
                        })
                    );
                    
                    resolve(res);
                })
                .catch((reason) => {
                    try {
                        let body = JSON.parse(reason._body);
                        if (body.error_description) {
                            let error = JSON.parse(body.error_description);
                            if (error.description) {
                                reject({
                                    status: reason.status,
                                    description: error.description,
                                });
                                return;
                            }
                        }
                    } catch (error) {}

                    reject({
                        status: reason.status,
                        description:
                            "The login operation failed. Please try again",
                    });
                });
        });

        return promise;
    }

    login(username: string, password: string): Promise<any> {
        const url = this.baseUrl + "identity/authenticate";
        let promise = new Promise((resolve, reject) => {
            this.http
                .post(url, { Username: username, Password: password })
                .toPromise()
                .then((res) => {
                    this.storeToken(res);
                    resolve(res);
                })
                .catch((reason) => {
                    try {
                        let body = JSON.parse(reason._body);
                        if (body.error_description) {
                            let error = JSON.parse(body.error_description);
                            if (error.description) {
                                reject({
                                    status: reason.status,
                                    description: error.description,
                                });
                                return;
                            }
                        }
                    } catch (error) {}

                    reject({
                        status: reason.status,
                        description:
                            "Your username or password are incorrect. Please try again.",
                    });
                });
        });

        return promise;
    }

    logout() {
        this.storageService.clear();
        this.storageService.clear(true);
        window.localStorage.clear();
        window.sessionStorage.clear();
        clearTimeout(this.refreshTimeout);
        window.location.assign("/identity/dologout");
    }

    requestResetPassword(email: string): Promise<any> {
        const url = this.baseUrl + "identity/requestresetpassword";
        let promise = new Promise((resolve, reject) => {
            this.http
                .post(url, { emailAddress: email })
                .toPromise()
                .then((res) => {
                    resolve(null);
                })
                .catch((reason) => {
                    reject({
                        status: reason.status,
                        description:
                            "Your reset password request failed. Please try again.",
                    });
                });
        });

        return promise;
    }

    resetPassword(
        guid1: string,
        guid2: string,
        password: string
    ): Promise<any> {
        const url = this.baseUrl + "identity/resetpassword";
        let promise = new Promise((resolve, reject) => {
            this.http
                .post(
                    url,
                    { guid1: guid1, guid2: guid2, NewPassword: password },
                    { responseType: "text" }
                )
                .toPromise()
                .then((res) => {
                    resolve(null);
                })
                .catch((reason) => {
                    reject({
                        status: reason.status,
                        description:
                            "Your password reset failed. Please try again.",
                    });
                });
        });

        return promise;
    }
}
